import React, { useState, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Heart, Percent, CheckCircle2, Clock, Loader2, X } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const DISCOUNT_PERCENTS = [0, 20, 30, 40, 50, 60];

/**
 * Selettore Sconto / Pro Bono - usato da admin/medico/ortottista.
 * @param {string} target - 'appointment' | 'check_in'
 * @param {string} targetId - appointment_id o check_in_id
 * @param {number} baseAmount - importo base in EUR
 * @param {number} initialDiscount - sconto attualmente applicato (0..60)
 * @param {boolean} initialProBono - pro bono attualmente applicato
 * @param {string} initialTiming - 'start' o 'end'
 * @param {function} onApplied - callback({amount_to_pay, manual_discount_percent, pro_bono, payment_timing})
 * @param {function} onClose - callback per chiudere il modale (opzionale)
 */
export const PaymentDiscountSelector = ({
  target = 'appointment',
  targetId,
  baseAmount = 0,
  initialDiscount = 0,
  initialProBono = false,
  initialTiming = 'end',
  onApplied,
  onClose,
}) => {
  const [proBono, setProBono] = useState(!!initialProBono);
  const [discountPct, setDiscountPct] = useState(initialProBono ? 0 : Number(initialDiscount) || 0);
  const [paymentTiming, setPaymentTiming] = useState(initialTiming || 'end');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const computed = useMemo(() => {
    if (proBono) return 0;
    const base = Number(baseAmount) || 0;
    return Math.round(base * (100 - discountPct)) / 100;
  }, [baseAmount, discountPct, proBono]);

  const handleApply = async () => {
    if (!targetId) {
      toast.error('ID elemento mancante');
      return;
    }
    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      const endpoint = target === 'check_in'
        ? `${API}/api/check-ins/${targetId}/apply-discount`
        : `${API}/api/appointments/${targetId}/apply-discount`;
      const body = proBono
        ? { pro_bono: true, payment_timing: paymentTiming, reason }
        : { discount_percent: discountPct, pro_bono: false, payment_timing: paymentTiming, reason };
      const res = await axios.post(endpoint, body, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      });
      if (res.data.success) {
        if (proBono) {
          toast.success('Pro bono applicato - nessun pagamento richiesto', { duration: 5000 });
        } else {
          toast.success(`Sconto ${discountPct}% applicato - importo: €${res.data.amount_to_pay.toFixed(2)}`, { duration: 5000 });
        }
        onApplied?.(res.data);
        onClose?.();
      }
    } catch (e) {
      console.error('Apply discount error', e);
      toast.error(e?.response?.data?.detail || 'Errore applicazione sconto');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-white rounded-xl border border-sky-200 p-5 space-y-4" data-testid="payment-discount-selector">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-[#2A9FCB] flex items-center gap-2">
          <Percent className="w-5 h-5" /> Sconto & Pagamento
        </h3>
        {onClose && (
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded" data-testid="payment-discount-close">
            <X className="w-4 h-4 text-gray-500" />
          </button>
        )}
      </div>

      {/* Quando si paga */}
      <div>
        <label className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-2 block">
          <Clock className="w-3 h-3 inline mr-1" /> Quando si paga
        </label>
        <div className="grid grid-cols-2 gap-2">
          <button
            type="button"
            onClick={() => setPaymentTiming('start')}
            className={`px-3 py-2 rounded-lg border text-sm font-medium transition ${
              paymentTiming === 'start'
                ? 'bg-[#2A9FCB] text-white border-[#2A9FCB]'
                : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
            }`}
            data-testid="timing-start-btn"
          >
            All&apos;inizio visita
          </button>
          <button
            type="button"
            onClick={() => setPaymentTiming('end')}
            className={`px-3 py-2 rounded-lg border text-sm font-medium transition ${
              paymentTiming === 'end'
                ? 'bg-[#2A9FCB] text-white border-[#2A9FCB]'
                : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
            }`}
            data-testid="timing-end-btn"
          >
            Alla fine visita
          </button>
        </div>
      </div>

      {/* Sconto */}
      <div>
        <label className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-2 block">
          <Percent className="w-3 h-3 inline mr-1" /> Sconto applicato
        </label>
        <div className="grid grid-cols-6 gap-2">
          {DISCOUNT_PERCENTS.map((p) => (
            <button
              key={p}
              type="button"
              onClick={() => { setDiscountPct(p); setProBono(false); }}
              disabled={proBono}
              className={`px-2 py-2 rounded-lg border text-sm font-medium transition ${
                !proBono && discountPct === p
                  ? 'bg-[#2A9FCB] text-white border-[#2A9FCB]'
                  : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed'
              }`}
              data-testid={`discount-${p}-btn`}
            >
              {p === 0 ? '0%' : `-${p}%`}
            </button>
          ))}
        </div>
      </div>

      {/* Pro Bono - SUBITO DOPO -60% */}
      <div>
        <button
          type="button"
          onClick={() => setProBono(v => !v)}
          className={`w-full px-3 py-3 rounded-lg border-2 text-sm font-bold transition flex items-center justify-center gap-2 ${
            proBono
              ? 'bg-rose-500 text-white border-rose-500 shadow-md'
              : 'bg-white text-rose-600 border-rose-300 hover:bg-rose-50'
          }`}
          data-testid="pro-bono-btn"
        >
          <Heart className={`w-4 h-4 ${proBono ? 'fill-white' : ''}`} />
          Pro bono - Nessun pagamento richiesto
          {proBono && <CheckCircle2 className="w-4 h-4" />}
        </button>
        {proBono && (
          <p className="text-xs text-rose-600 mt-2 text-center" data-testid="pro-bono-info">
            Importo da pagare: €0.00 - non verrà creata richiesta Stripe.
          </p>
        )}
      </div>

      {/* Motivo (opzionale) */}
      <div>
        <label className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-1 block">
          Motivo (opzionale)
        </label>
        <input
          type="text"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder={proBono ? 'es. paziente in difficoltà, beneficenza...' : 'es. cliente fidelizzato, promo...'}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
          data-testid="discount-reason-input"
        />
      </div>

      {/* Riepilogo importo */}
      <div className="bg-gradient-to-br from-sky-50 to-blue-50 rounded-lg p-4 border border-sky-200">
        <div className="flex justify-between items-center text-sm text-gray-600">
          <span>Importo base:</span>
          <span className="font-medium">€ {Number(baseAmount || 0).toFixed(2)}</span>
        </div>
        {!proBono && discountPct > 0 && (
          <div className="flex justify-between items-center text-sm text-green-700 mt-1">
            <span>Sconto -{discountPct}%:</span>
            <span className="font-medium">- € {((Number(baseAmount) || 0) * discountPct / 100).toFixed(2)}</span>
          </div>
        )}
        {proBono && (
          <div className="flex justify-between items-center text-sm text-rose-700 mt-1">
            <span>Pro bono:</span>
            <span className="font-medium">- € {Number(baseAmount || 0).toFixed(2)}</span>
          </div>
        )}
        <div className="border-t border-sky-300 mt-2 pt-2 flex justify-between items-center">
          <span className="text-sm font-semibold text-gray-800">Importo da pagare:</span>
          <span className={`text-2xl font-bold ${proBono ? 'text-rose-600' : 'text-[#2A9FCB]'}`} data-testid="amount-to-pay">
            € {computed.toFixed(2)}
          </span>
        </div>
      </div>

      <button
        type="button"
        onClick={handleApply}
        disabled={submitting || !targetId}
        className="w-full px-4 py-3 rounded-lg bg-[#2A9FCB] text-white font-semibold hover:bg-[#1E6E99] disabled:opacity-50 flex items-center justify-center gap-2"
        data-testid="apply-discount-btn"
      >
        {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
        {proBono ? 'Conferma Pro bono' : 'Applica sconto'}
      </button>
    </div>
  );
};

/**
 * Badge che mostra lo stato pagamento (incluso Pro bono).
 */
export const PaymentStatusBadge = ({ proBono, paymentStatus, amountToPay, discountPercent }) => {
  if (proBono || paymentStatus === 'pro_bono') {
    return (
      <span
        className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold bg-rose-100 text-rose-700 border border-rose-300"
        data-testid="pro-bono-badge"
      >
        <Heart className="w-3 h-3 fill-rose-500 text-rose-500" /> Pro bono
      </span>
    );
  }
  if (paymentStatus === 'paid' || paymentStatus === 'covered') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-700 border border-green-300">
        <CheckCircle2 className="w-3 h-3" /> Pagato
      </span>
    );
  }
  if (discountPercent && discountPercent > 0) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold bg-amber-100 text-amber-700 border border-amber-300">
        <Percent className="w-3 h-3" /> -{discountPercent}% (€ {Number(amountToPay || 0).toFixed(2)})
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-700 border border-gray-300">
      € {Number(amountToPay || 0).toFixed(2)}
    </span>
  );
};

export default PaymentDiscountSelector;
